import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Concepto 4: Timeline interactivo
// Journey bar en la parte superior muestra el recorrido H→E→R.
// Cada fase se puede activar tocando — ilumina el nodo
// y resalta la fila correspondiente.

const phases = [{
  id: 'H',
  days: 'DÍAS 1—7',
  name: 'Hormonal',
  tagline: 'Empezás a sentir menos hinchazón y más claridad.',
  description: 'Acompañar el equilibrio hormonal y ayudar a que tu cuerpo vuelva a responder a nuevas señales.\n\nEmpezás a sentir menos hinchazón, más claridad y una sensación de mayor equilibrio.',
  detail: [{
    label: 'Objetivo',
    text: 'Regularizar el cortisol y restablecer el ritmo de sueño.'
  }, {
    label: 'Alimentación',
    text: 'Alimentos que modulan la señal hormonal base.'
  }, {
    label: 'Resultado',
    text: 'Menos inflamación. Sueño más profundo.'
  }]
}, {
  id: 'E',
  days: 'DÍAS 8—14',
  name: 'Energía',
  tagline: 'Energía sostenida. Digestiones más livianas.',
  description: 'Acompañar la digestión, el equilibrio del cortisol y el uso eficiente de energía.\n\nEmpezás a notar más energía, digestiones más livianas y una sensación de mayor estabilidad durante el día.',
  detail: [{
    label: 'Objetivo',
    text: 'Recuperar energía sostenida sin estimulantes.'
  }, {
    label: 'Alimentación',
    text: 'Protocolo de nutrientes para el eje tiroideo y suprarrenal.'
  }, {
    label: 'Resultado',
    text: 'Claridad mental. Energía que dura hasta la tarde.'
  }]
}, {
  id: 'R',
  days: 'DÍAS 15—21',
  name: 'Restaurativa',
  tagline: 'El cuerpo empieza a autorregularse.',
  description: 'Consolidar cambios y crear un nuevo punto de equilibrio.\n\nEmpezás a sentir más confianza, más estabilidad y una forma más sostenible de cuidarte.\n\nNo es el final — es el comienzo de una nueva relación con tu cuerpo.',
  detail: [{
    label: 'Objetivo',
    text: 'Integrar el protocolo como hábito natural.'
  }, {
    label: 'Alimentación',
    text: 'Flexibilidad metabólica. El cuerpo comienza a autorregularse.'
  }, {
    label: 'Resultado',
    text: 'La nueva forma de sentirte empieza a ser la norma.'
  }]
}];
export function ProtocolSection() {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const toggle = (i: number) => setActiveIdx(prev => prev === i ? null : i);
  return <section className="bg-gradient-section py-20 md:py-28 px-5 md:px-8" data-id="element-275">
      <div className="max-w-2xl mx-auto" data-id="element-276">

        {/* Intro */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="text-center mb-14 md:mb-16" data-id="element-277">
          <h2 className="font-display font-semibold text-[#F0E8D8] leading-[1.1]" style={{
          fontSize: 'clamp(30px, 6vw, 48px)'
        }} data-id="element-278">
            El método <span className="italic text-gradient-gold" data-id="element-279">H·E·R</span>
          </h2>
          <p className="uppercase tracking-[0.28em] font-body mt-4 text-[#F0E8D8]/70 text-[12px]" style={{
          fontFamily: 'sans-serif'
        }} data-id="element-280">
            Tres fases · 21 días · Esta etapa
          </p>
        </motion.div>

        {/* Journey bar — H ─── E ─── R */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="flex items-center mb-14" data-id="element-281">
          {phases.map((phase, i) => <React.Fragment key={phase.id} data-id="element-282">
              {/* Node */}
              <button onClick={() => toggle(i)} className={`flex flex-col items-center gap-1.5 group transition-all duration-400 flex-shrink-0`} data-id="element-283">
                <div className={`w-9 h-9 rounded-full border flex items-center justify-center text-[12px] font-bold transition-all duration-400
                  ${activeIdx === i ? 'border-[#C8A870] text-[#0a0807] bg-[#C8A870]' : 'border-[rgba(200,168,112,0.35)] text-[#C8A870] bg-transparent group-hover:border-[#C8A870]/60'}`} style={{
              fontFamily: 'sans-serif'
            }} data-id="element-284">
                  {phase.id}
                </div>
                <span className={`text-[10px] tracking-[0.12em] transition-colors duration-300
                    ${activeIdx === i ? 'text-[#C8A870]' : 'text-[rgba(200,168,112,0.45)]'}`} style={{
              fontFamily: 'sans-serif'
            }} data-id="element-285">
                  {phase.name}
                </span>
              </button>

              {/* Line between nodes */}
              {i < phases.length - 1 && <div className={`flex-1 h-px mx-2 transition-all duration-500
                    ${activeIdx !== null && activeIdx > i ? 'bg-[#C8A870]' : 'bg-[rgba(200,168,112,0.15)]'}`} data-id="element-286" />}
            </React.Fragment>)}
        </motion.div>

        {/* Phase rows — editorial + interactive */}
        <div data-id="element-287">
          {phases.map((phase, i) => <motion.div key={phase.id} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: i * 0.1
        }} className={`group relative py-10 md:py-12 cursor-pointer transition-all duration-300
                ${i !== 0 ? 'border-t-[0.5px] border-gold/12' : ''}
                ${activeIdx === i ? 'border-l-2 border-[#C8A870] pl-5 md:pl-7 -ml-5 md:-ml-7' : ''}
              `} onClick={() => toggle(i)} data-id="element-288">
              {/* Giant ghost letter */}
              <span className={`absolute -top-2 left-0 font-display font-medium select-none pointer-events-none transition-all duration-300
                ${activeIdx === i ? 'text-gold/25' : 'text-gold/10'}`} style={{
            fontSize: 'clamp(80px, 18vw, 130px)',
            lineHeight: 1
          }} data-id="element-289">
                {phase.id}
              </span>

              <div className="relative pl-1" data-id="element-290">
                <p className="text-right uppercase tracking-[0.2em] text-[11px] text-[#C8A870] font-semibold" style={{
              fontFamily: 'sans-serif'
            }} data-id="element-291">
                  {phase.days}
                </p>

                {/* Gold tick */}
                <div className={`h-px bg-gold/70 mt-10 md:mt-12 mb-4 transition-all duration-300
                  ${activeIdx === i ? 'w-14 bg-gold' : 'w-8 group-hover:w-12 group-hover:bg-gold/80'}`} data-id="element-292" />

                {/* Name */}
                <h3 className={`font-display font-medium leading-tight transition-colors duration-300
                  ${activeIdx === i ? 'text-[#C8A870]' : 'text-[#E8D5B0] group-hover:text-gold-light'}`} style={{
              fontSize: 'clamp(24px, 5vw, 32px)'
            }} data-id="element-293">
                  {phase.name}
                </h3>

                {/* Description siempre visible */}
                <p className="font-body mt-3 max-w-md text-[#F0E8D8]/75 whitespace-pre-line" style={{
              fontSize: 'clamp(14px, 2.5vw, 16px)',
              lineHeight: 1.7
            }} data-id="element-294">
                  {phase.description}
                </p>

                {/* Detalle interactivo — se expande al tocar */}
                <div className="overflow-hidden transition-all duration-500" style={{
              maxHeight: activeIdx === i ? 240 : 0,
              opacity: activeIdx === i ? 1 : 0
            }} data-id="element-295">
                  <div className="mt-5 space-y-3 border-t border-[rgba(200,168,112,0.12)] pt-5" data-id="element-296">
                    {phase.detail.map(row => <div key={row.label} className="flex gap-3" data-id="element-297">
                        <span className="text-[10px] uppercase tracking-[0.12em] text-[#C8A870] mt-0.5 flex-shrink-0" style={{
                    fontFamily: 'sans-serif',
                    minWidth: 80
                  }} data-id="element-298">
                          {row.label}
                        </span>
                        <span className="text-[#F0E8D8]/70 text-[13px]" style={{
                    fontFamily: 'sans-serif',
                    lineHeight: 1.5
                  }} data-id="element-299">
                          {row.text}
                        </span>
                      </div>)}
                  </div>
                </div>

                {/* Hint de interacción */}
                {activeIdx !== i && <p className="mt-3 text-[11px] text-[#C8A870]/40 tracking-[0.1em] transition-opacity duration-300 group-hover:text-[#C8A870]/70" style={{
              fontFamily: 'sans-serif'
            }} data-id="element-300">
                    Tocá para ver el detalle →
                  </p>}
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}